import React from 'react';
import Avatar from '../../../../components/shared/Avatar/Avatar';
import { FeedPost } from '../../../types/feed';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import { getComments, createComment, PostComment } from '../../../../services/posts/api';
import { getPublicProfile, PublicProfile } from '../../../../services/social/api';
import './FeedPostCard.css';

interface FeedPostCardProps {
  post: FeedPost;
  currentUserId?: string;
  onLike?: (postId: string) => void;
  onComment?: (postId: string) => void;
  onShare?: (postId: string) => void;
  onEdit?: (postId: string) => void;
  onDelete?: (postId: string) => void;
}

// Commenter display names, resolved once and shared across every card.
const nameCache = new Map<string, string>();

const displayName = (profile: PublicProfile | null): string => {
  if (!profile) return 'Traveler';
  const full = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
  return full || profile.nickname || 'Traveler';
};

const resolveCommenterName = async (userId: string): Promise<string> => {
  const cached = nameCache.get(userId);
  if (cached) return cached;
  try {
    const name = displayName(await getPublicProfile(userId));
    nameCache.set(userId, name);
    return name;
  } catch {
    return 'Traveler';
  }
};

const PREVIEW_COMMENT_LIMIT = 3;

const FeedPostCard: React.FC<FeedPostCardProps> = ({
  post,
  currentUserId = '',
  onLike,
  onComment,
  onShare,
  onEdit,
  onDelete,
}) => {
  const [replyText, setReplyText] = React.useState('');
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const avatarSrc = post.author.avatarUrl || post.author.avatarEmoji;
  const galleryImages = post.imageUrls || (post.imageUrl ? [post.imageUrl] : []);
  const canReply = replyText.trim().length > 0;
  const isOwner = Boolean(currentUserId) && post.author.id === currentUserId;
  const showPostMenu = isOwner && (onEdit || onDelete);

  React.useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Comments (T2.10d). Preview the latest few on load; expand for the full thread.
  const [comments, setComments] = React.useState<PostComment[]>([]);
  const [commentNames, setCommentNames] = React.useState<Record<string, string>>({});
  const [commentsExpanded, setCommentsExpanded] = React.useState(false);
  const [commentsLoaded, setCommentsLoaded] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [commentCount, setCommentCount] = React.useState(post.comments);
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);

  const closeLightbox = React.useCallback(() => setLightboxIndex(null), []);
  const showPreviousPhoto = React.useCallback(() => {
    setLightboxIndex((current) => {
      if (current === null || galleryImages.length <= 1) return current;
      return (current - 1 + galleryImages.length) % galleryImages.length;
    });
  }, [galleryImages.length]);
  const showNextPhoto = React.useCallback(() => {
    setLightboxIndex((current) => {
      if (current === null || galleryImages.length <= 1) return current;
      return (current + 1) % galleryImages.length;
    });
  }, [galleryImages.length]);

  React.useEffect(() => {
    if (lightboxIndex === null) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') showPreviousPhoto();
      if (event.key === 'ArrowRight') showNextPhoto();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeLightbox, lightboxIndex, showNextPhoto, showPreviousPhoto]);

  React.useEffect(() => {
    setCommentCount(post.comments);
  }, [post.comments]);

  const resolveNames = React.useCallback(async (rows: PostComment[]) => {
    const ids = Array.from(new Set(rows.map((c) => c.authorId)));
    const entries = await Promise.all(ids.map(async (id) => [id, await resolveCommenterName(id)] as const));
    setCommentNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  }, []);

  const loadComments = React.useCallback(async () => {
    setLoadingComments(true);
    setCommentError(null);
    try {
      const page = await getComments(post.id, post.author.id);
      setComments(page.comments);
      setCommentsLoaded(true);
      void resolveNames(page.comments);
    } catch {
      setCommentError('Could not load comments.');
    } finally {
      setLoadingComments(false);
    }
  }, [post.id, post.author.id, resolveNames]);

  React.useEffect(() => {
    if (commentCount <= 0 || commentsLoaded || loadingComments) return;
    void loadComments();
  }, [commentCount, commentsLoaded, loadingComments, loadComments]);

  const visibleComments = commentsExpanded
    ? comments
    : comments.slice(-PREVIEW_COMMENT_LIMIT);
  const hasHiddenComments = comments.length > PREVIEW_COMMENT_LIMIT && !commentsExpanded;
  const showCommentsSection = loadingComments || (commentsLoaded && comments.length > 0);

  const handleCommentAction = () => {
    if (hasHiddenComments) {
      setCommentsExpanded(true);
    }
    onComment?.(post.id);
  };

  const handleReplySubmit = async () => {
    if (!canReply || submitting) return;
    setSubmitting(true);
    setCommentError(null);
    try {
      const comment = await createComment(post.id, post.author.id, replyText.trim());
      setComments((prev) => [...prev, comment]);
      setCommentCount((prev) => prev + 1);
      setCommentsLoaded(true);
      void resolveNames([comment]);
      setReplyText('');
    } catch {
      setCommentError('Could not post your comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="v2-feed-post-card">
      <header className="v2-feed-post-card__header">
        <div className="v2-feed-post-card__author">
          <Avatar
            src={avatarSrc}
            alt={post.author.name}
            size="medium"
            className="v2-feed-post-card__avatar"
          />
          <div className="v2-feed-post-card__author-info">
            <span className="v2-feed-post-card__author-name">{post.author.name}</span>
            <span className="v2-feed-post-card__location">{post.location}</span>
          </div>
        </div>
        <time className="v2-feed-post-card__time" dateTime={post.createdAt}>
          {formatRelativeTime(post.createdAt)}
        </time>
        {showPostMenu && (
          <div className="v2-feed-post-card__menu" ref={menuRef}>
            <button
              type="button"
              className="v2-feed-post-card__menu-trigger"
              aria-label="Post options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.75" />
                <circle cx="12" cy="12" r="1.75" />
                <circle cx="12" cy="19" r="1.75" />
              </svg>
            </button>
            {menuOpen && (
              <div className="v2-feed-post-card__menu-dropdown" role="menu" aria-label="Post options">
                {onEdit && (
                  <button
                    type="button"
                    role="menuitem"
                    className="v2-feed-post-card__menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(post.id);
                    }}
                  >
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    role="menuitem"
                    className="v2-feed-post-card__menu-item v2-feed-post-card__menu-item--danger"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete(post.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {post.badge && (
          <button type="button" className="v2-feed-post-card__badge">
            {post.badge}
          </button>
        )}
      </header>

      {(galleryImages.length > 0 || post.imageEmoji) && (
        <div className={`v2-feed-post-card__media ${galleryImages.length > 1 ? 'v2-feed-post-card__media--gallery' : ''}`}>
          {galleryImages.length > 0 ? (
            galleryImages.slice(0, 3).map((imageUrl, index) => (
              <button
                type="button"
                key={`${imageUrl}-${index}`}
                className={`v2-feed-post-card__image-wrap v2-feed-post-card__image-wrap--${index + 1}`}
                onClick={() => setLightboxIndex(index)}
                aria-label={`Open photo ${index + 1} of ${galleryImages.length}`}
              >
                <img src={imageUrl} alt="" className="v2-feed-post-card__image" />
                {index === 2 && galleryImages.length > 3 && (
                  <span className="v2-feed-post-card__more">+{galleryImages.length - 3}</span>
                )}
                {index === 2 && post.savedCount && galleryImages.length <= 3 && (
                  <span className="v2-feed-post-card__more">+{post.savedCount}</span>
                )}
              </button>
            ))
          ) : (
            <span className="v2-feed-post-card__image-placeholder">{post.imageEmoji}</span>
          )}
        </div>
      )}

      <p className="v2-feed-post-card__caption">{post.caption}</p>

      <footer className="v2-feed-post-card__actions">
        <button
          type="button"
          className={`v2-feed-post-card__action ${post.isLiked ? 'v2-feed-post-card__action--active' : ''}`}
          onClick={() => onLike?.(post.id)}
          aria-label={`Like post, ${post.likes} likes`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={post.isLiked ? 'currentColor' : 'none'}
            />
          </svg>
          <span>{post.likes}</span>
        </button>
        <button
          type="button"
          className="v2-feed-post-card__action"
          onClick={handleCommentAction}
          aria-expanded={commentsExpanded}
          aria-label={`Comment, ${commentCount} comments`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{commentCount} replies</span>
        </button>
        <button
          type="button"
          className="v2-feed-post-card__action"
          onClick={() => onShare?.(post.id)}
          aria-label="Share post"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="16 6 12 2 8 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Share</span>
        </button>
      </footer>

      {showCommentsSection && (
        <div className="v2-feed-post-card__comments">
          {loadingComments && <p className="v2-feed-post-card__comments-status">Loading comments…</p>}
          {visibleComments.map((comment) => (
            <div key={comment.commentId} className="v2-feed-post-card__comment">
              <span className="v2-feed-post-card__comment-author">
                {commentNames[comment.authorId] || 'Traveler'}
              </span>
              <span className="v2-feed-post-card__comment-text">{comment.text}</span>
              <time className="v2-feed-post-card__comment-time" dateTime={comment.createdAt}>
                {formatRelativeTime(comment.createdAt)}
              </time>
            </div>
          ))}
          {hasHiddenComments && (
            <button
              type="button"
              className="v2-feed-post-card__comments-more"
              onClick={() => setCommentsExpanded(true)}
            >
              View all {commentCount} replies
            </button>
          )}
        </div>
      )}

      {commentError && <p className="v2-feed-post-card__comment-error">{commentError}</p>}

      <div className="v2-feed-post-card__reply">
        <input
          type="text"
          className="v2-feed-post-card__reply-input"
          placeholder="Write a reply..."
          value={replyText}
          onChange={(event) => setReplyText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleReplySubmit();
            }
          }}
          aria-label={`Reply to ${post.author.name}`}
        />
        <button
          type="button"
          className="v2-feed-post-card__reply-submit"
          onClick={handleReplySubmit}
          disabled={!canReply || submitting}
        >
          {submitting ? 'Posting…' : 'Reply'}
        </button>
      </div>

      {lightboxIndex !== null && galleryImages[lightboxIndex] && (
        <div
          className="v2-feed-post-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${lightboxIndex + 1} of ${galleryImages.length}`}
        >
          <button
            type="button"
            className="v2-feed-post-lightbox__backdrop"
            onClick={closeLightbox}
            aria-label="Close photo viewer"
          />
          <button
            type="button"
            className="v2-feed-post-lightbox__close"
            onClick={closeLightbox}
            aria-label="Close photo viewer"
          >
            ×
          </button>
          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                className="v2-feed-post-lightbox__nav v2-feed-post-lightbox__nav--prev"
                onClick={showPreviousPhoto}
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                type="button"
                className="v2-feed-post-lightbox__nav v2-feed-post-lightbox__nav--next"
                onClick={showNextPhoto}
                aria-label="Next photo"
              >
                ›
              </button>
              <div className="v2-feed-post-lightbox__counter">
                {lightboxIndex + 1} / {galleryImages.length}
              </div>
            </>
          )}
          <img
            src={galleryImages[lightboxIndex]}
            alt=""
            className="v2-feed-post-lightbox__image"
          />
        </div>
      )}
    </article>
  );
};

export default FeedPostCard;
